<script>
import { Bar } from "vue-chartjs";
import Chart from "chart.js";
import ChartAnnotationsPlugin from "chartjs-plugin-annotation";
import moment from "moment/moment";
Chart.plugins.register(ChartAnnotationsPlugin);

export default {
  extends: Bar,
  name: "SnowChart",
  props: {
    selectedObservatory: {
      type: Object
    },
    currentDate: String
  },
  data() {
    return {
      dataCollection: {
        labels: [],
        datasets: [
          {
            type: "line",
            label: "積雪深",
            unit: "cm",
            pointBackgroundColor: "white",
            fill: false,
            yAxisID: "snow",
            xAxisID: "data",
            borderWidth: 2,
            borderColor: "#91C46C",
            backgroundColor: "#91C46C",
            pointBorderColor: "#91C46C",
            pointRadius: 2,
            pointHitRadius: 0,
            lineTension: 0,
            data: [],
            spanGaps: false
          },
          {
            type: "bar",
            label: "時間降雪",
            yAxisID: "snow",
            xAxisID: "data",
            unit: "cm",
            backgroundColor: "#1C344C",
            tooltipLabelColor: "#1C344C",
            borderColor: "#1C344C",
            pointBackgroundColor: "white",
            borderWidth: 0.5,
            data: []
          },
          {
            type: "line",
            label: "気温",
            unit: "℃",
            pointBackgroundColor: "white",
            fill: false,
            yAxisID: "temperature",
            xAxisID: "data",
            borderWidth: 2,
            borderColor: "yellow",
            backgroundColor: "yellow",
            pointBorderColor: "yellow",
            pointRadius: 2,
            pointHitRadius: 0,
            lineTension: 0,
            data: [],
            spanGaps: false
          },
        ]
      },
      options: {
        layout: {
          padding: {
            right: 0
          }
        },
        tooltips: {
          enabled: true,
          mode: "index",
          intersect: false,
          callbacks: {
            label: function(tooltipItem, data) {
              const label = data.datasets[tooltipItem.datasetIndex].label;
              const unit = data.datasets[tooltipItem.datasetIndex].unit;
              const val =
                  data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
              if (val !== null) {
                return label + " : " + val + " " + unit;
              }
            }
          }
        },
        scales: {
          yAxes: [
            {
              id: "snow",
              position: "left",
              gridLines: {
                display: false
              },
              ticks: {
                max: 20,
                min: 0,
                stepSize: 1,
                callback: label => {
                  return parseFloat(label).toFixed(0);
                }
              }
            },
            {
              id: "temperature",
              position: "right",
              gridLines: {
                display: true
              },
              ticks: {
                max: 20,
                min: 0,
                callback: label => {
                  return parseFloat(label).toFixed(1);
                }
              }
            },
          ],
          xAxes: [
            {
              id: "data",
              type: "time",
              gridLines: {
                display: false
              },
              time: {
                unit: "hour",
                unitStepSize: 1,
                tooltipFormat: "MM/DD HH:mm",
                displayFormats: {
                  minute: "HH:mm",
                  hour: "HH:mm",
                  day: "HH:mm",
                  week: "HH:mm",
                  month: "HH:mm"
                }
              },
              ticks: {
                display: true,
                autoSkip: true
              }
            }
          ]
        },
        responsive: true,
        maintainAspectRatio: false,
        hover: {
          mode: "index",
          intersect: false
        },
        annotation: {
          events: ["click", "dblclick", "mouseover", "mouseout"],
          annotations: [
            {
              type: "line",
              mode: "vertical",
              scaleID: "data",
              value: "02:00",
              borderColor: "#FF0000",
              borderWidth: 1.5,
              label: {
                xAdjust:20,
                content: "現在",
                position: "top",
                enabled: true,
              },

            }
          ]
        },
        legend: {
          display: false
        }
      }
    };
  },
  computed: {
    snowVolumes() {
      if (this.selectedObservatory.timeSeries === undefined) {
        return [];
      }
      return this.selectedObservatory.timeSeries
    },
    baseDate() {
      return moment.utc(this.currentDate, "YYYY/MM/DD HH:mm");
    },
  },
  watch: {
    selectedObservatory() {
      this.setTimeSeries();
      this.setYAxesLimitData();
      this.renderChart(this.dataCollection, this.options);
    },
  },
  mounted() {
    this.setTimeSeries();
    this.setYAxesLimitData();
    setTimeout(() => {
      this.renderChart(this.dataCollection, this.options);
    }, 500);
  },
  methods: {
    setTimeSeries() {
      if (this.snowVolumes.length === 0) {
        this.dataCollection.labels = [];
        this.dataCollection.datasets[0] = [];
        this.dataCollection.datasets[1] = [];
        this.dataCollection.datasets[2] = [];
        return;
      }

      // labels
      this.dataCollection.labels = this.snowVolumes.map(ts => {
        return moment
            .utc(ts.date, "YYYY/MM/DD HH:mm")
            .local()
            .toDate();
      });

      this.dataCollection.datasets[0].data = this.snowVolumes.map(ts => {
        return ts.snow_total === undefined ? null : ts.snow_total.toFixed(2);
      });

      this.dataCollection.datasets[1].data = this.snowVolumes.map(ts => {
        return ts.snow === undefined ? null : ts.snow.toFixed(2);
      });
      this.dataCollection.datasets[2].data = this.snowVolumes.map(ts => {
        return ts.atmospheric_temperature === undefined ? null : ts.atmospheric_temperature.toFixed(2);
      });
      // annotations
      this.options.annotation.annotations[0].value = this.baseDate
          .local()
          .toDate();
    },
    setYAxesLimitData() {
      const totalMaxLevel = Math.max(
          ...this.snowVolumes.map(r => (r.snow_total !== undefined ? r.snow_total : 0))
      );

      const temperatureMinLevel = Math.min(
          ...this.snowVolumes.map(r => (r.atmospheric_temperature !== undefined ? r.atmospheric_temperature : 0))
      );
      const temperatureMaxLevel = Math.max(
          ...this.snowVolumes.map(r => (r.atmospheric_temperature !== undefined ? r.atmospheric_temperature : 0))
      );

      this.options.scales.yAxes[0].ticks.min = 0;
      this.options.scales.yAxes[0].ticks.max = Math.floor(totalMaxLevel + 10);

      this.options.scales.yAxes[1].ticks.min = Math.floor(temperatureMinLevel - 0.5);
      this.options.scales.yAxes[1].ticks.max = Math.floor(temperatureMaxLevel + 10);
    }
  }
};
</script>

<style scoped></style>
